import './CheckList.css';
import { IonAvatar, IonIcon } from '@ionic/react';
import React, { useContext, useState } from 'react';

import { DEFAULT_AVATAR_URL } from '../../model/models';
import moment from 'moment';
import ChecklistTask from './ChecklistTask';
import { useMediaQuery } from 'react-responsive';
import { AppContext } from '../../state/AppContextProvider';
import { useChecklistEntry } from '../../hooks/ChecklistHooks';
import { useHistory } from 'react-router';
import { arrowForwardOutline } from 'ionicons/icons';
import { FormattedMessage } from 'react-intl';
import { useLogger } from '../../services/LogService';
import { ChecklistService } from '../../services/ChecklistService';
import {
  ChecklistEntryUpdate,
  WP_Checklist,
  WP_ChecklistEntry,
} from '../../model/checklist/checklist';

interface CheckListProps {
  checklist: WP_Checklist;
  checklist_entry: WP_ChecklistEntry;
  redirectionDisplay: boolean;
  alignTextLeft: boolean;
  paddingBottom: boolean;
  heightFull: boolean;
}

const CheckList: React.FC<CheckListProps> = ({
  checklist,
  checklist_entry,
  redirectionDisplay,
  alignTextLeft,
  paddingBottom,
  heightFull,
}) => {
  const { state } = useContext(AppContext);

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const history = useHistory();

  const logger = useLogger('CheckList');

  const { update } = useChecklistEntry();

  const tasks = checklist_entry?.acf?.entry_checklist_tasks || [];

  const tmpAllTasksIds = (checklist.acf?.checklist_tasks || []).map(
    (t) => t.checklist_tasks_key
  );

  // filter task ids only checked and still valid (existing in original checklist)
  const tmpCheckedTasksIds = tasks
    .filter(
      (t) =>
        t.entry_checklist_tasks_value === true &&
        tmpAllTasksIds.includes(t.entry_checklist_tasks_unique_id)
    )
    .map((t) => t.entry_checklist_tasks_unique_id);

  const [checkedTasks, setCheckedTasks] = useState(tmpCheckedTasksIds);

  const days_diff = moment(
    moment(checklist_entry?.acf.entry_checklist_end_valid_date, 'DD/MM/YYYY')
  ).diff(new Date(), 'days');

  const radius = isMobile ? 30 : 52;
  // let circumference = Math.PI * (radius * 2);
  const numberOfCheckbox =
    ChecklistService.getChecklistTotalTasksCount(checklist);

  const numberOfCheckboxChecked =
    ChecklistService.getChecklistCheckedTasksCount(checklist, checklist_entry);

  // let pourcent = (numberOfCheckboxChecked * 100) / numberOfCheckbox;

  // const progress = (circumference, pourcent) => {
  //   let strokeDashoffset = circumference * (1 - pourcent / 100);
  //   return strokeDashoffset;
  // };

  // let [progressionBar, setProgressionBar] = useState(
  //   progress(circumference, pourcent)
  // );

  const redirectionFunction = () => {
    if (redirectionDisplay) {
      history.push('/profile/checklists');
    }
  };

  const updateChecklist = (taskId: string, checked: boolean) => {
    if (!checklist_entry?.acf?.entry_checklist_tasks) {
      return;
    }

    logger.info(
      `updateChecklist() checklist: ${checklist.id}, task: ${taskId}, checked: ${checked}`
    );

    // compute next local state
    let newCheckedTasks = checkedTasks;
    if (checked && !checkedTasks.includes(taskId)) {
      newCheckedTasks = [...checkedTasks, taskId];
    } else if (checkedTasks.includes(taskId)) {
      newCheckedTasks = checkedTasks.filter((t) => t !== taskId);
    }

    logger.info(
      `updateChecklist() checklist: ${
        checklist.id
      }, task: ${taskId}, checked in local state: ${newCheckedTasks.includes(
        taskId
      )}`
    );

    // update local state
    setCheckedTasks(newCheckedTasks);

    // update global state and save
    const newEntryTasks =
      (checklist?.acf?.checklist_tasks || []).map((task) => {
        return {
          entry_checklist_tasks_unique_id: task.checklist_tasks_key,
          entry_checklist_tasks_value: newCheckedTasks.includes(
            task.checklist_tasks_key
          ),
          entry_checklist_tasks_title: task.checklist_tasks_title,
        };
      }) || [];

    const numberOfCheckbox =
      ChecklistService.getChecklistTotalTasksCount(checklist);

    const checklistEntryUpdate: ChecklistEntryUpdate = {
      acf: {
        entry_checklist_tasks: newEntryTasks,
        entry_checklist_status:
          newCheckedTasks.length >= numberOfCheckbox ? 'done' : 'in_progress',
      },
    };

    update(checklist.id, checklist_entry?.id || 0, checklistEntryUpdate);
  };

  return (
    <div
      className={`checklist bg-card rounded-3xl text-white mb-8 ${
        redirectionDisplay && 'flex flex-col justify-between cursor-pointer'
      } ${heightFull && 'h-full'}`}
      onClick={redirectionFunction}
    >
      <div
        className={`grid grid-cols-12 px-10 ${
          paddingBottom ? 'py-12' : 'pt-12'
        }`}
      >
        <div className="col-span-10 xl:flex xl:flex-col xl:justify-between xl:col-span-8">
          <h1 className="text-2xl text-left font-bold">
            {checklist.acf.checklist_title}
          </h1>
          <div className="mt-2">
            <p className={`text-primary ${alignTextLeft && 'text-left'}`}>
              <FormattedMessage id={'The checklist ends in'} />
              <span> {days_diff} </span>
              <FormattedMessage id={'day'} />
              {days_diff > 1 ? 's' : ''}
            </p>
            <h4 className="text-sm text-left text-white italic mb-4 xl:hidden">
              {checklist.acf.checklist_description}
            </h4>
          </div>
        </div>
        <div className="flex items-center flex-col col-span-2 xl:col-span-4">
          <div className="pourcentage relative w-full flex items-center justify-center">
            {/* ${isDone ? 'hidden' : 'block'} */}
            <IonAvatar className={`absolute w-11 h-11 md:w-20 md:h-20 `}>
              <img
                className="w-11 h-full checklist-avatar md:w-20 md:h-20"
                src={
                  state.user?.acf.photo !== undefined &&
                  state.user?.acf.photo !== ''
                    ? state.user?.acf.photo
                    : DEFAULT_AVATAR_URL
                }
                alt=""
              />
            </IonAvatar>
            <svg className="circle">
              <circle
                className="bg"
                cx={isMobile ? '32' : '57'}
                cy={isMobile ? '32' : '57'}
                r={radius}
              />
              {/* <circle className={`meter-1 animate-progress`} cx="57" cy="57" r={radius} strokeDasharray={circumference} strokeDashoffset={progressionBar}/> */}
            </svg>
          </div>
          <p className="text-primary text-center">
            <span className="font-bold">{numberOfCheckboxChecked}</span>
            <span className="opacity-60"> / {numberOfCheckbox}</span>
          </p>
        </div>
        <div className="w-full col-span-12 mt-2">
          <h4 className="hidden text-sm text-left text-white italic mb-4 xl:block">
            {checklist.acf.checklist_description}
          </h4>
          <ul className="relative">
            {checklist.acf.checklist_tasks &&
              checklist.acf.checklist_tasks.map((task, id) => {
                return (
                  <ChecklistTask
                    key={id}
                    disabled={false}
                    checked={checkedTasks.includes(task.checklist_tasks_key)}
                    task={task}
                    updateChecklistEntry={updateChecklist}
                    redirectionDisplay={redirectionDisplay}
                    redirectionFunction={redirectionFunction}
                  />
                );
              })}
          </ul>
        </div>
      </div>
      {redirectionDisplay && (
        <div className="w-full flex flex-row-reverse">
          <div className="p-4">
            <div>
              <IonIcon
                className="bg-primary stroke-current text-white top-1/2 p-2 text-lg rounded-full"
                icon={arrowForwardOutline}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckList;
